<script lang="ts" setup>
import {computed, reactive, ref, useTemplateRef} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import Vue3Signature from 'vue3-signature'
import {useQuizStore} from '@/stores/quizStore';
import { appendData } from '@/services/quizService'
import {gtmSigned, mktLeadSigned} from "@/helpers/marketingEvents.ts";
import DobSelect from "@/components/Ui/Form/DobSelect.vue"
import DpfAndPcpSummaryModal from "@/components/Quiz/DpfAndPcpSummaryModal.vue"
import { removeWhiteBackground, validateSignaturePng } from '@/helpers/image-manipulation.ts'

const currentYear = new Date().getFullYear();
const minimumAge = 17;
const minYear = currentYear - minimumAge;

const {
  contact,
  signature: storeSignature,
  selectedVehicles,
  isFetching,
  getStoredData,
  setIsFetching,
  setSignature,
  setContact,
  showAlert,
  setCreditReportAuthenticationMessage,
  handleAddressLookup,
  goToStepPath,
  setAgreementsCount,
  aagLeadToken,
  setClientPortalUrl,
  leadSource,
  consentMarketing,
} = useQuizStore()

const signaturePadWrapper = useTemplateRef<HTMLDivElement>('signaturePadWrapper')

const state = reactive({
  count: 0,
  disabled: false
})

const signature = ref(null)
const dob = ref<string>(contact.dob ?? '')
const signatureIsValid = ref(!!storeSignature.value);
const showErrors = ref(false)
const isValid = computed(() => dob.value && signatureIsValid.value)

const clear = () => {
  (signature.value as any)?.clear()
  signatureIsValid.value = false;
}

const validateSignature = async () => {
  if (!signature.value || (signature.value as any).isEmpty()) {
    signatureIsValid.value = false;
    return;
  }
  const signaturePng = (signature.value as any)?.save('image/png', 'base64')
  signatureIsValid.value = await validateSignaturePng(signaturePng);
}

const signaturePngWithTransparentBackground = async () => {
  const signaturePng = (signature.value as any)?.save('image/png', 'base64')

  try {
    return await removeWhiteBackground(signaturePng)
  } catch (e) {
    console.error(e)
    return signaturePng
  }
}

const handleSubmit = async () => {
  if (!isValid.value) {
    showErrors.value = true
    return;
  }

  const payload = getStoredData()
  payload.contact = {
    ...payload.contact,
    dob: dob.value
  }
  const signatureWasChanged = !(signature.value as any)?.isEmpty()
  if (signatureWasChanged) {
    payload.signature = await signaturePngWithTransparentBackground()
  }

  setIsFetching(true)
  appendData(payload)
    .then((response) => {
      setContact(payload.contact)
      setSignature(payload.signature)

      const {
        next_action,
        data: {agreements_count, client_portal_url, message}
      } = response

      setClientPortalUrl(client_portal_url || '')

      gtmSigned(payload.email, payload.contact.phone)

      aagLeadToken.value.pcp.forEach((token) => {
        mktLeadSigned(token, payload.email, payload.contact.phone, payload.contact.first_name,
          payload.contact.last_name, payload.contact.title, leadSource.value, 'PCP', consentMarketing.value)
      })

      aagLeadToken.value.dpf.forEach((token) => {
        mktLeadSigned(token, payload.email, payload.contact.phone, payload.contact.first_name,
          payload.contact.last_name, payload.contact.title, leadSource.value, 'DPF', consentMarketing.value)
      })

      if (next_action === 'show-credit-report') {
        setAgreementsCount(agreements_count)
        goToStepPath(9)
        setIsFetching(false)
        return
      }

      if (next_action === 'request-credit-report') {
        setCreditReportAuthenticationMessage(message)
        handleAddressLookup()
        return
      }
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on appendData: SignaturePad.vue");
        captureException(err);
      }
      setIsFetching(false)
    })
}
</script>

<template>
  <div v-if="selectedVehicles.length">
    <img src="@/assets/icons/trophy-icon.webp" alt="trophy" class="h-20 mx-auto py-2"/>

    <h1 class="text-xl font-bold mb-3 text-center">
      <span class="text-(--brand-primary) ">You could be owed $1,000s</span><br>Ready to find
      even more?
    </h1>

    <p class="mb-4 border-b border-b-gray-200 pb-4 text-sm">
      By signing, you allow us to process the claim we've found and continue searching for even more
      compensation.
    </p>
  </div>
  <div v-else>
    <img src="@/assets/icons/claim-icon.webp" alt="trophy" class="h-20 mx-auto py-2"/>

    <h1 class="text-xl font-bold mb-3 text-center">Ready to claim what's yours?<br>This is the
      final step.</h1>

    <p class="mb-4 border-b border-b-gray-200 pb-4 text-sm">
      By signing, you authorise us to begin running your claims. You’re one step from uncovering
      what you might be owed
    </p>
  </div>

  <p class="mb-3 text-sm">Please tell us your date of birth</p>

  <DobSelect v-model="dob" :minYear="minYear" :showErrors="showErrors"/>

  <h2 class="text-xl font-bold text-gray-900 mb-2">Please Sign Here</h2>

  <p class="text-gray-600 mb-4">
    Sign below to see how many of your agreements may be eligible for compensation.
  </p>

  <div
    ref="signaturePadWrapper"
    class="border border-gray-300 rounded-lg flex items-center justify-center mb-4 overflow-hidden"
    :class="showErrors && !signatureIsValid ? 'border-red-500': ''"
    @pointerleave="validateSignature">
    <Vue3Signature ref="signature" h="128px"
                   :disabled="state.disabled" :defaultUrl="storeSignature"/>
  </div>

  <p class="text-gray-600 mb-4">Signed with an intention to enter into a legally binding agreement
    with JLG Legal Limited t/a Claims for Cars.</p>

  <div class="flex space-x-3 mb-4">
    <button @click="clear" :disabled="isFetching" id="signature-pad-clear-button"
            class="w-1/3 py-2 px-4 border border-gray-300 rounded-lg text-gray-600 uppercase text-sm font-medium cursor-pointer transition">
      Clear
    </button>
    <button @click="handleSubmit" id="signature-pad-submit-button"
            class="w-2/3 py-2 px-4 bg-green-500 text-white rounded-lg uppercase text-sm font-medium cursor-pointer disabled:cursor-not-allowed disabled:text-gray-100 disabled:bg-(--brand-primary)/90 flex items-center justify-center gap-1 transition"
            :disabled="isFetching">
      <svg v-if="isFetching" class="mr-3 -ml-1 size-5 animate-spin text-white"
           xmlns="http://www.w3.org/2000/svg"
           fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
        </path>
      </svg>
      <span>Submit and Verify My Claim</span>
    </button>
  </div>

  <DpfAndPcpSummaryModal/>

  <div class="text-xs text-gray-500 space-y-4">
    <p>
      By pressing the "Submit and Verify My Claim" button, I confirm that I financed a vehicle after
      2007 and that I was not aware of any commission payment made to the dealer. I have read and
      agree to JLG’s terms and conditions and privacy policy. I understand that in order to verify
      my eligibility and comply with ‘Know Your Client’ checks, JLG will conduct a soft credit check
      through its providers, Checkboard and Verify365, which will not affect my credit score. I
      consent to JLG sending a Letter of Complaint to the lenders to determine if my PCP or HP
      agreements were mis-sold and to present my claim.
    </p>
    <p class="underline">
      You also consent to your electric signature being used for the purposes of validating forms
      authority confirming we are acting your behalf and any additional client retainers for
      additional car finance claims we identify that you may qualify for.
    </p>
    <p>I confirm that I am not an undischarged bankrupt or have entered into an individual voluntary
      arrangement at the time of the entering into this agreement.</p>
  </div>
</template>
